class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.setHeaderCartIconAccessibility();

    // OBA cart upsells
    this.addEventListener(
      'click',
      this.handleObaUpsellClick.bind(this)
    );

    this.addEventListener(
      'change',
      this.handleObaUpsellChange.bind(this)
    );
  }


  /* =========================================================
     HEADER CART ICON
  ========================================================= */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();

      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();

        this.open(cartLink);
      }
    });
  }


  /* =========================================================
     OPEN DRAWER
  ========================================================= */

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add(
        'animate',
        'active'
      );
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      {
        once: true
      }
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector(
      'cart-drawer-items'
    )?.dispatchViewEvent();
  }


  /* =========================================================
     CLOSE DRAWER
  ========================================================= */

  close() {
    this.classList.remove('active');

    removeTrapFocus(this.activeElement);

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* =========================================================
     SUMMARY ACCESSIBILITY
  ========================================================= */

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling?.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.getAttribute('id')
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    cartDrawerNote.parentElement?.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }


  /* =========================================================
     RENDER CART CONTENTS
  ========================================================= */

  renderContents(parsedState) {
    const drawerInner = this.querySelector(
      '.drawer__inner'
    );

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement =
        section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

      if (
        !sectionElement ||
        !parsedState.sections?.[section.id]
      ) {
        return;
      }

      sectionElement.innerHTML =
        this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
    });

    if (
      typeof parsedState.item_count === 'number'
    ) {
      this.classList.toggle(
        'is-empty',
        parsedState.item_count === 0
      );
    } else {
      this.classList.remove('is-empty');
    }

    setTimeout(() => {
      const newOverlay =
        this.querySelector('#CartDrawer-Overlay');

      if (newOverlay) {
        newOverlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      this.open();
    });
  }


  /* =========================================================
     GET SECTION HTML
  ========================================================= */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsed =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const source =
      parsed.querySelector(selector);

    return source
      ? source.innerHTML
      : '';
  }


  /* =========================================================
     SECTIONS TO UPDATE
  ========================================================= */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }


  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }


  setActiveElement(element) {
    this.activeElement = element;
  }


  /* =========================================================
     OBA UPSELL CLICK
  ========================================================= */

  handleObaUpsellClick(event) {
    const addButton =
      event.target.closest(
        '[data-oba-upsell-add]'
      );

    if (
      addButton &&
      this.contains(addButton)
    ) {
      event.preventDefault();

      this.addObaUpsell(addButton);

      return;
    }
  }


  /* =========================================================
     OBA CHECKBOX UPSELL
  ========================================================= */

  handleObaUpsellChange(event) {
    const checkbox =
      event.target.closest(
        '[data-oba-checkbox-upsell]'
      );

    if (
      !checkbox ||
      !this.contains(checkbox)
    ) {
      return;
    }

    this.toggleObaCheckboxUpsell(
      checkbox
    );
  }


  /* =========================================================
     ADD COLLECTION UPSELL
  ========================================================= */

  async addObaUpsell(button) {
    if (
      button.disabled ||
      button.classList.contains('is-loading')
    ) {
      return;
    }

    const variantId =
      Number(button.dataset.variantId);

    if (!variantId) return;

    button.disabled = true;

    button.classList.add(
      'is-loading'
    );

    try {
      const state =
        await this.obaCartRequest(
          routes.cart_add_url,
          {
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ]
          }
        );

      this.renderContents(state);
    } catch (error) {
      this.showObaCartError(error);

      button.disabled = false;

      button.classList.remove(
        'is-loading'
      );
    }
  }


  /* =========================================================
     CHECKBOX UPSELL ADD / REMOVE
  ========================================================= */

  async toggleObaCheckboxUpsell(
    checkbox
  ) {
    if (checkbox.disabled) return;

    const variantId =
      Number(
        checkbox.dataset.variantId
      );

    const lineKey =
      checkbox.dataset.lineKey;

    const shouldAdd =
      checkbox.checked;

    checkbox.disabled = true;

    checkbox
      .closest('.oba-cart-addon')
      ?.classList.add('is-loading');

    try {
      let state;

      if (shouldAdd) {
        state =
          await this.obaCartRequest(
            routes.cart_add_url,
            {
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: {
                    _oba_footer_upsell: '1'
                  }
                }
              ]
            }
          );
      } else {
        if (!lineKey) {
          throw new Error(
            'Upsell line key is missing.'
          );
        }

        state =
          await this.obaCartRequest(
            routes.cart_change_url,
            {
              id: lineKey,
              quantity: 0
            }
          );
      }

      this.renderContents(state);
    } catch (error) {
      checkbox.checked =
        !shouldAdd;

      checkbox.disabled = false;

      checkbox
        .closest('.oba-cart-addon')
        ?.classList.remove(
          'is-loading'
        );

      this.showObaCartError(error);
    }
  }


  /* =========================================================
     OBA AJAX CART REQUEST
  ========================================================= */

  async obaCartRequest(
    endpoint,
    payload
  ) {
    const sections =
      this
        .getSectionsToRender()
        .map(
          (section) => section.id
        );

    const body =
      JSON.stringify({
        ...payload,
        sections,
        sections_url:
          window.location.pathname
      });

    const response =
      await fetch(
        endpoint,
        {
          ...fetchConfig(),
          body
        }
      );

    const state =
      await response.json();

    if (
      !response.ok ||
      state.status ||
      state.errors
    ) {
      const message =
        state.description ||
        state.message ||
        state.errors ||
        window.cartStrings?.error ||
        'Cart update failed.';

      throw new Error(
        typeof message === 'string'
          ? message
          : 'Cart update failed.'
      );
    }

    return state;
  }


  /* =========================================================
     SHOW CART ERROR
  ========================================================= */

  showObaCartError(error) {
    console.error(error);

    const errors =
      this.querySelector(
        '#CartDrawer-CartErrors'
      );

    if (errors) {
      errors.textContent =
        error?.message ||
        window.cartStrings?.error ||
        'Cart update failed.';
    }
  }
}


/* =========================================================
   REGISTER CART DRAWER
========================================================= */

customElements.define(
  'cart-drawer',
  CartDrawer
);


/* =========================================================
   CART DRAWER ITEMS

   IMPORTANT:
   This overrides Dawn's normal quantity update ONLY
   inside the cart drawer.

   It uses the same AJAX method as the working OBA
   upsell buttons.
========================================================= */

class CartDrawerItems extends CartItems {


  /* =======================================================
     SECTIONS TO RENDER
  ======================================================= */

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }


  /* =======================================================
     FIX CART DRAWER QUANTITY UPDATE
  ======================================================= */

  async updateQuantity(
    line,
    quantity,
    event,
    name,
    variantId
  ) {
    const drawer =
      this.closest('cart-drawer');

    if (!drawer) return;


    /* -----------------------------------------------------
       Find current quantity input
    ----------------------------------------------------- */

    const quantityInput =
      this.querySelector(
        `#Drawer-quantity-${line}`
      ) ||
      this.querySelector(
        `#Quantity-${line}`
      );


    /* -----------------------------------------------------
       Exact Shopify cart line key
    ----------------------------------------------------- */

    const lineKey =
      quantityInput
        ?.dataset
        .quantityLineKey;


    /* -----------------------------------------------------
       Variant ID
    ----------------------------------------------------- */

    const lineVariantId =
      variantId ||
      quantityInput
        ?.dataset
        .quantityVariantId;


    const nextQuantity =
      Number(quantity);


    if (
      Number.isNaN(nextQuantity)
    ) {
      return;
    }


    /* -----------------------------------------------------
       Clear previous error
    ----------------------------------------------------- */

    const errors =
      drawer.querySelector(
        '#CartDrawer-CartErrors'
      );

    if (errors) {
      errors.textContent = '';
    }


    /* -----------------------------------------------------
       Disable controls / show loader
    ----------------------------------------------------- */

    try {
      this.enableLoading(line);
    } catch (error) {
      console.warn(
        'Could not enable cart loading state:',
        error
      );
    }


    try {

      /* ---------------------------------------------------
         IMPORTANT

         We use Shopify's LINE KEY instead of relying only
         on line number.

         This is important because you have:
         - normal cart products
         - collection upsells
         - hidden checkbox upsell product

         A numeric line position can become unreliable.
      --------------------------------------------------- */

      const payload =
        lineKey
          ? {
              id: lineKey,
              quantity:
                nextQuantity
            }
          : {
              line:
                Number(line),
              quantity:
                nextQuantity
            };


      /* ---------------------------------------------------
         Use SAME AJAX system as your working upsells
      --------------------------------------------------- */

      const state =
        await drawer.obaCartRequest(
          routes.cart_change_url,
          payload
        );


      /* ---------------------------------------------------
         Completely refresh drawer sections
      --------------------------------------------------- */

      drawer.renderContents(
        state
      );


      /* ---------------------------------------------------
         Let Dawn / other theme scripts know cart changed.

         Wrapped so another subscriber cannot destroy
         the successful cart update.
      --------------------------------------------------- */

      try {
        publish(
          PUB_SUB_EVENTS.cartUpdate,
          {
            source:
              'cart-items',

            cartData:
              state,

            variantId:
              lineVariantId
          }
        );
      } catch (publishError) {
        console.warn(
          'Cart updated successfully, but cartUpdate publish failed:',
          publishError
        );
      }

    } catch (error) {

      /* ---------------------------------------------------
         Actual Shopify AJAX error
      --------------------------------------------------- */

      console.error(
        'Cart quantity update failed:',
        error
      );


      /* Restore old number */

      if (quantityInput) {
        quantityInput.value =
          quantityInput.getAttribute(
            'value'
          );
      }


      drawer.showObaCartError(
        error
      );

    } finally {

      /* ---------------------------------------------------
         The drawer may already have been replaced,
         therefore protect this call.
      --------------------------------------------------- */

      try {
        this.disableLoading(
          line
        );
      } catch (error) {
        // Old element may already be removed.
      }
    }
  }
}


/* =========================================================
   REGISTER CART DRAWER ITEMS
========================================================= */

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);